import { FC, useState } from 'react';
import {
  Button,
  Stack,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import PaperSection from '../../../components/PaperSection';
import { deleteUser } from '../../../services/danger';
import { useAuth } from '../../../contexts/AuthContext';
import { UserRole } from '@avanzu/types';
import { useNavigate } from 'react-router-dom';
import { leaveOrganisation } from '../../../services/team';

export const DangerZoneRoles = [UserRole.AVANZU, UserRole.OWNER];

const DangerZone: FC = () => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const { currentOrganisation, organisationId, logout, refreshUserOrganisationState } = useAuth();

  const handleDeleteAccount = async () => {
    if (confirmText !== 'DELETE') {
      setError('Please type "DELETE" to confirm');
      return;
    }

    setIsDeleting(true);
    setError(null);

    const result = await deleteUser(organisationId);

    if (!result.success) {
      setError(result.message || 'An error occurred while deleting the account');
      setIsDeleting(false);
    } else {
      logout();
      navigate('/login');
    }
  };

  const handleLeaveOrganisation = async () => {
    if (confirmText !== 'LEAVE') {
      setError('Please type "LEAVE" to confirm');
      return;
    }

    if (!organisationId) {
      setError('Error loading organisation. Please try again.');
      return;
    }

    // if (currentOrganisation?.role === UserRole.OWNER) {
    //   setError('Owners cannot leave the organisation, you must transfer ownership first.');
    //   return;
    // }

    setIsLeaving(true);
    setError(null);

    try {
      const result = await leaveOrganisation(organisationId);

      if (!result.success) {
        setError(result.message || 'An error occurred while leaving the organisation');
        setIsLeaving(false);
      } else {
        await refreshUserOrganisationState();
        navigate('/select-organisation');
      }
    } catch (error) {
      setError('An error occurred while leaving the organisation');
      setIsLeaving(false);
    }
  };

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
    setConfirmText('');
    setError(null);
  };
  const openLeaveDialog = () => {
    setLeaveDialogOpen(true);
    setConfirmText('');
    setError(null);
  };

  return (
    <PaperSection title="Danger Zone">
      <Stack spacing={3}>
        <Box>
          <Button variant="outlined" color="error" onClick={openLeaveDialog} disabled={isLeaving}>
            {isLeaving ? 'Leaving...' : 'Leave Organisation'}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This action is permanent and will remove you from this organisation
          </Typography>
        </Box>

        <Box>
          <Button variant="outlined" color="error" onClick={openDeleteDialog} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This action is permanent and will delete all your data
          </Typography>
        </Box>
      </Stack>
      {/* Leave Organisation Dialog */}
      <Dialog
        open={leaveDialogOpen}
        onClose={() => setLeaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Leave {currentOrganisation?.name || 'this organisation'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              This action will permanently remove you from{' '}
              <strong>{currentOrganisation?.name || 'this organisation'}</strong>. You will lose
              access to all data associated with this organisation.
            </Typography>
            {currentOrganisation?.role === UserRole.OWNER && (
              <Alert severity="error">
                As the owner of this organisation, this action will permanently delete the
                organisation and all its data
              </Alert>
            )}

            <Alert severity="warning">
              This action cannot be undone. Please be certain before proceeding.
            </Alert>
            <TextField
              fullWidth
              name="leaveConfirmation"
              id="leaveConfirmation"
              label='Type "LEAVE" to confirm'
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={isLeaving}
            />
            {error && (
              <Alert severity="error" variant="filled">
                {error}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveDialogOpen(false)} disabled={isLeaving}>
            Cancel
          </Button>
          <Button
            onClick={handleLeaveOrganisation}
            color="error"
            variant="contained"
            disabled={confirmText !== 'LEAVE' || isLeaving}
          >
            {isLeaving ? 'Leaving...' : 'Leave Organisation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              This action will permanently delete your account and all associated data, including:
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              <li>All information in organisations you are the owner of</li>
              <li>
                All R&D plans, transactions, and reports in organisations you are the owner of
              </li>
              <li>All invitations and notifications in organisations you are the owner of</li>
              <li>Your user profile and settings</li>
              <li>All linked third-party services</li>
            </Box>
            <Alert severity="warning">
              This action cannot be undone. Please be certain before proceeding.
            </Alert>
            <TextField
              fullWidth
              name="deleteConfirmation"
              id="deleteConfirmation"
              label='Type "DELETE" to confirm'
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={isDeleting}
            />
            {error && (
              <Alert severity="error" variant="filled">
                {error}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={confirmText !== 'DELETE' || isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </PaperSection>
  );
};

export default DangerZone;
